import {
  Check,
  ChevronDown,
  CircleAlert,
  Copy,
  ExternalLink,
  FileJson,
  KeyRound,
  Link2,
  LoaderCircle,
  RefreshCw,
  Settings2,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import { useCallback, useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import type {
  BrowserLoginStartResponse,
  CodexAccount,
  CodexAuthBlob,
  CodexAuthResponse,
  DevicePollResponse,
  DeviceStartResponse,
} from '../../shared/api';
import {
  ApiRequestError,
  completeCodexBrowserLogin,
  isAbortError,
  pollCodexDeviceLogin,
  saveCodexAuth,
  startCodexBrowserLogin,
  startCodexDeviceLogin,
} from '../lib/api';
import { copyText } from '../lib/clipboard';
import { formatDuration } from '../lib/format';
import { useNow } from '../lib/hooks';
import { CODEX_TEXTS, codexLoginFailureText, planLabel, rateLimitText } from '../lib/texts';
import { Dialog } from './Dialog';

export interface CodexConnectedResult {
  account: CodexAccount;
  /** Only for anonymous users: to be stored in localStorage. */
  auth?: CodexAuthBlob;
}

export interface CodexLoginDialogProps {
  open: boolean;
  signedIn: boolean;
  /** A summary waits for the login (changes the wording slightly). */
  resumePending: boolean;
  /** Called as soon as the login succeeded (store the auth here). */
  onConnected: (result: CodexConnectedResult) => void;
  /** Called when the dialog should close; `connected` is true after a successful login. */
  onClose: (connected: boolean) => void;
}

const SUCCESS_CLOSE_DELAY_MS = 1400;

/** German text for a failed request to our server (the technical message goes to the details). */
function requestFailureText(error: unknown, fallback: string): string {
  if (error instanceof ApiRequestError) {
    if (error.code === 'network') return 'Der Server ist nicht erreichbar. Prüfe deine Internetverbindung.';
    if (error.code === 'rate_limited') return rateLimitText(error.retryAfter);
  }
  return fallback;
}

function technicalDetail(error: unknown): string | undefined {
  if (error instanceof ApiRequestError) return `${error.code}${error.status ? ` (HTTP ${error.status})` : ''}: ${error.message}`;
  return error instanceof Error ? error.message : undefined;
}

/** Error box with German text; the server's (English) message only under "Technische Details". */
function LoginError({ id, message, detail, children }: { id?: string; message: string; detail?: string | undefined; children?: ReactNode }) {
  return (
    <div id={id} className="inline-error" role="alert">
      <CircleAlert size={18} aria-hidden="true" />
      <div className="inline-error__body">
        <span>{message}</span>
        {detail && detail !== message && (
          <details className="tech-details">
            <summary>Technische Details</summary>
            <code>{detail}</code>
          </details>
        )}
        {children}
      </div>
    </div>
  );
}

/**
 * ChatGPT (Codex) login. Primary: device code. Collapsed alternatives: sign-in via the redirect URL
 * (works without the ChatGPT device-code setting) and pasting a Codex CLI auth.json.
 */
export function CodexLoginDialog({ open, signedIn, resumePending, onConnected, onClose }: CodexLoginDialogProps) {
  const [connected, setConnected] = useState<CodexAccount | null>(null);
  const [alternative, setAlternative] = useState<'browser' | 'json' | null>(null);

  // Reset when (re)opened.
  useEffect(() => {
    if (open) {
      setConnected(null);
      setAlternative(null);
    }
  }, [open]);

  const handleConnected = useCallback(
    (result: CodexConnectedResult) => {
      setConnected(result.account);
      onConnected(result);
    },
    [onConnected],
  );

  useEffect(() => {
    if (!connected) return;
    const timer = window.setTimeout(() => onClose(true), SUCCESS_CLOSE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [connected, onClose]);

  return (
    <Dialog
      open={open}
      onClose={() => onClose(!!connected)}
      title={CODEX_TEXTS.title}
      description={CODEX_TEXTS.intro}
      icon={<Sparkles size={22} />}
      dismissOnBackdrop={false}
      initialFocus="[data-autofocus]"
    >
      {connected ? (
        <div className="codex-success" role="status">
          <span className="codex-success__icon" aria-hidden="true">
            <Check size={28} strokeWidth={2.5} />
          </span>
          <p className="codex-success__title">Verbunden!</p>
          <p className="codex-success__text">
            {connected.email ? `Angemeldet als ${connected.email}` : 'Dein ChatGPT-Konto ist verbunden'}
            {planLabel(connected.plan) ? ` (${planLabel(connected.plan)})` : ''}.
            {resumePending ? ' Die Zusammenfassung startet jetzt.' : ''}
          </p>
        </div>
      ) : (
        <>
          {/* A login is bound to the session that started it: restart when the app sign-in changes. */}
          <DeviceLogin key={signedIn ? 'user' : 'anon'} active={open} onConnected={handleConnected} />

          <Disclosure
            className="codex-more"
            summary={CODEX_TEXTS.moreOptions}
            onToggle={(isOpen, element) => {
              if (isOpen) window.requestAnimationFrame(() => element.scrollIntoView({ block: 'nearest', behavior: 'smooth' }));
            }}
          >
            <div className="codex-alts">
              <Disclosure
                className="codex-alt"
                name="codex-alternative"
                icon={<Link2 size={18} />}
                summary={CODEX_TEXTS.browserTitle}
                subtitle={CODEX_TEXTS.browserSubtitle}
                onToggle={(isOpen) => setAlternative((current) => (isOpen ? 'browser' : current === 'browser' ? null : current))}
              >
                <BrowserLogin
                  key={signedIn ? 'user' : 'anon'}
                  active={open && alternative === 'browser'}
                  onConnected={handleConnected}
                />
              </Disclosure>
              <Disclosure
                className="codex-alt"
                name="codex-alternative"
                icon={<FileJson size={18} />}
                summary={CODEX_TEXTS.authJsonTitle}
                subtitle={CODEX_TEXTS.authJsonSubtitle}
                onToggle={(isOpen) => setAlternative((current) => (isOpen ? 'json' : current === 'json' ? null : current))}
              >
                <AuthJsonLogin onConnected={handleConnected} />
              </Disclosure>
            </div>
          </Disclosure>

          <p className="dialog__footnote">
            <KeyRound size={14} aria-hidden="true" />
            {signedIn ? CODEX_TEXTS.storedInAccount : CODEX_TEXTS.storedInBrowser}
          </p>
        </>
      )}
    </Dialog>
  );
}

// ---------------------------------------------------------------------------

/** A <details> element with a styled summary row. */
function Disclosure({
  summary,
  subtitle,
  icon,
  name,
  className,
  children,
  onToggle,
}: {
  summary: ReactNode;
  subtitle?: ReactNode;
  icon?: ReactNode;
  /** Exclusive accordion group (only one open at a time). */
  name?: string;
  className?: string;
  children: ReactNode;
  onToggle?: (open: boolean, element: HTMLDetailsElement) => void;
}) {
  return (
    <details
      className={`disclosure${className ? ` ${className}` : ''}`}
      name={name}
      onToggle={(event) => onToggle?.(event.currentTarget.open, event.currentTarget)}
    >
      <summary className="disclosure__summary">
        {icon && (
          <span className="disclosure__icon" aria-hidden="true">
            {icon}
          </span>
        )}
        <span className="disclosure__text">
          <span className="disclosure__title">{summary}</span>
          {subtitle && <span className="disclosure__subtitle">{subtitle}</span>}
        </span>
        <ChevronDown size={18} className="disclosure__chevron" aria-hidden="true" />
      </summary>
      <div className="disclosure__body">{children}</div>
    </details>
  );
}

// ---------------------------------------------------------------------------

type DeviceState =
  | { kind: 'starting' }
  | { kind: 'waiting'; device: DeviceStartResponse }
  | { kind: 'failed'; message: string; detail?: string };

function failedState(result: Exclude<DevicePollResponse, { status: 'pending' | 'complete' }>, method: 'device' | 'browser'): DeviceState {
  return { kind: 'failed', message: codexLoginFailureText(result.status, result.message, method), detail: result.message };
}

function DeviceLogin({ active, onConnected }: { active: boolean; onConnected: (result: CodexConnectedResult) => void }) {
  const [state, setState] = useState<DeviceState>({ kind: 'starting' });
  const [attempt, setAttempt] = useState(0);
  const [copied, setCopied] = useState(false);
  const onConnectedRef = useRef(onConnected);
  useEffect(() => {
    onConnectedRef.current = onConnected;
  });

  useEffect(() => {
    if (!active) return;
    const controller = new AbortController();
    const { signal } = controller;
    let timer = 0;
    setState({ kind: 'starting' });
    setCopied(false);

    const run = async () => {
      let device: DeviceStartResponse;
      try {
        device = await startCodexDeviceLogin(signal);
      } catch (error) {
        if (isAbortError(error) || signal.aborted) return;
        const detail = technicalDetail(error);
        setState({
          kind: 'failed',
          message: requestFailureText(error, 'Die Anmeldung konnte nicht gestartet werden. Bitte versuche es erneut.'),
          ...(detail ? { detail } : {}),
        });
        return;
      }
      if (signal.aborted) return;
      setState({ kind: 'waiting', device });

      const intervalMs = Math.max(1, device.interval || 5) * 1000;
      const expiresAt = Date.parse(device.expiresAt);
      let failures = 0;

      const poll = async () => {
        if (signal.aborted) return;
        if (Number.isFinite(expiresAt) && Date.now() > expiresAt) {
          setState({ kind: 'failed', message: CODEX_TEXTS.deviceExpired });
          return;
        }
        try {
          const result = await pollCodexDeviceLogin(device.loginId, signal);
          if (signal.aborted) return;
          failures = 0;
          if (result.status === 'complete') {
            onConnectedRef.current({ account: result.account, ...(result.auth ? { auth: result.auth } : {}) });
            return;
          }
          if (result.status !== 'pending') return setState(failedState(result, 'device'));
        } catch (error) {
          if (isAbortError(error) || signal.aborted) return;
          if (error instanceof ApiRequestError && error.code === 'rate_limited') {
            // Polled too often: wait as asked, this is not a failure.
            timer = window.setTimeout(poll, Math.max(intervalMs, (error.retryAfter ?? 0) * 1000));
            return;
          }
          // Transient network errors: keep polling for a while.
          if (++failures >= 5) {
            const detail = technicalDetail(error);
            setState({ kind: 'failed', message: 'Die Verbindung zum Server ist unterbrochen.', ...(detail ? { detail } : {}) });
            return;
          }
        }
        timer = window.setTimeout(poll, intervalMs);
      };
      timer = window.setTimeout(poll, intervalMs);
    };
    void run();

    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [active, attempt]);

  const waiting = state.kind === 'waiting' ? state.device : null;
  const now = useNow(!!waiting);
  const remaining = waiting ? Math.max(0, Date.parse(waiting.expiresAt) - now) : 0;

  const copyCode = async () => {
    if (!waiting) return;
    const ok = await copyText(waiting.userCode);
    setCopied(ok);
  };

  const setupHint = (
    <Disclosure className="codex-setup" icon={<Settings2 size={16} />} summary={CODEX_TEXTS.deviceSetupTitle}>
      <p>
        In ChatGPT: <strong>Einstellungen → Sicherheit → „Gerätecode-Autorisierung für Codex“</strong> einschalten.
      </p>
      <p className="codex-setup__subtle">{CODEX_TEXTS.deviceSetupWorkspace}</p>
      <a className="codex-setup__link" href={CODEX_TEXTS.chatGptSecurityUrl} target="_blank" rel="noopener noreferrer">
        ChatGPT-Einstellungen öffnen
        <ExternalLink size={14} aria-hidden="true" />
      </a>
    </Disclosure>
  );

  if (state.kind === 'failed') {
    return (
      <div className="device device--failed">
        <LoginError message={state.message} detail={state.detail} />
        <button type="button" className="btn btn--primary" data-autofocus onClick={() => setAttempt((n) => n + 1)}>
          <RefreshCw size={18} aria-hidden="true" />
          Neuen Code anfordern
        </button>
        {setupHint}
      </div>
    );
  }

  return (
    <div className="device">
      {setupHint}
      <ol className="device__steps">
        <li>
          <span className="device__num" aria-hidden="true">
            1
          </span>
          <div className="device__step-body">
            <p>{CODEX_TEXTS.deviceStep1}</p>
            <a
              className={`btn btn--primary${waiting ? '' : ' is-disabled'}`}
              href={waiting?.verificationUrl ?? '#'}
              target="_blank"
              rel="noopener noreferrer"
              data-autofocus
              aria-disabled={!waiting}
              title="Öffnet ChatGPT in einem neuen Tab und kopiert den Code"
              onClick={(event) => {
                if (!waiting) {
                  event.preventDefault();
                  return;
                }
                void copyCode();
              }}
            >
              ChatGPT-Anmeldung öffnen
              <ExternalLink size={17} aria-hidden="true" />
            </a>
          </div>
        </li>
        <li>
          <span className="device__num" aria-hidden="true">
            2
          </span>
          <div className="device__step-body">
            <p>{CODEX_TEXTS.deviceStep2}</p>
            <div className="device__code-row">
              {waiting ? (
                <output className="device__code" aria-label={`Code: ${waiting.userCode.split('').join(' ')}`}>
                  {waiting.userCode}
                </output>
              ) : (
                <span className="device__code device__code--loading" aria-label="Code wird angefordert">
                  <LoaderCircle size={24} className="spin" aria-hidden="true" />
                </span>
              )}
              <button
                type="button"
                className={`btn btn--secondary btn--sm${copied ? ' is-success' : ''}`}
                onClick={copyCode}
                disabled={!waiting}
              >
                {copied ? <Check size={17} aria-hidden="true" /> : <Copy size={17} aria-hidden="true" />}
                <span aria-live="polite">{copied ? 'Kopiert' : 'Kopieren'}</span>
              </button>
            </div>
            <p className="device__warning">
              <ShieldCheck size={15} aria-hidden="true" />
              <span>{CODEX_TEXTS.deviceWarning}</span>
            </p>
          </div>
        </li>
      </ol>
      <div className="device__status">
        {waiting ? (
          <>
            <LoaderCircle size={16} className="spin" aria-hidden="true" />
            <span role="status">{CODEX_TEXTS.deviceWaiting}</span>
            <span className="device__expiry" title="So lange ist der Code gültig">
              noch {formatDuration(remaining / 1000)} min
            </span>
          </>
        ) : (
          <span role="status">Code wird angefordert …</span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

type BrowserState =
  | { kind: 'starting' }
  | { kind: 'ready'; login: BrowserLoginStartResponse }
  | { kind: 'failed-start'; message: string; detail: string | undefined };

/** The address of the callback page contains the authorization code (or an OAuth error). */
function looksLikeCallback(text: string): boolean {
  return /[?&#](code|error)=/.test(text);
}

function BrowserLogin({ active, onConnected }: { active: boolean; onConnected: (result: CodexConnectedResult) => void }) {
  const [state, setState] = useState<BrowserState>({ kind: 'starting' });
  const [attempt, setAttempt] = useState(0);
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ message: string; detail?: string; restart?: boolean } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fieldId = useId();
  const errorId = useId();

  useEffect(() => {
    if (!active) return;
    const controller = new AbortController();
    setState({ kind: 'starting' });
    setError(null);
    startCodexBrowserLogin(controller.signal)
      .then((login) => setState({ kind: 'ready', login }))
      .catch((err: unknown) => {
        if (isAbortError(err) || controller.signal.aborted) return;
        setState({
          kind: 'failed-start',
          message: requestFailureText(err, 'Die Anmeldung konnte nicht gestartet werden. Bitte versuche es erneut.'),
          detail: technicalDetail(err),
        });
      });
    return () => controller.abort();
  }, [active, attempt]);

  const login = state.kind === 'ready' ? state.login : null;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const text = url.trim();
    if (!login) return;
    if (!looksLikeCallback(text)) {
      setError({
        message: text
          ? 'Das ist nicht die gesuchte Adresse. Sie beginnt mit http://127.0.0.1:1455/auth/callback?code=…'
          : 'Füge die Adresse der Seite ein, auf der du nach der Anmeldung gelandet bist.',
      });
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await completeCodexBrowserLogin(login.loginId, text);
      if (result.status === 'complete') {
        onConnected({ account: result.account, ...(result.auth ? { auth: result.auth } : {}) });
        return;
      }
      if (result.status === 'pending') return; // not used by this endpoint
      setError({
        message: codexLoginFailureText(result.status, result.message, 'browser'),
        detail: result.message,
        restart: result.status === 'expired' || /different sign-in|another session|invalid login/i.test(result.message),
      });
    } catch (err) {
      const detail = technicalDetail(err);
      setError({
        message: requestFailureText(err, 'Die Anmeldung konnte nicht abgeschlossen werden. Bitte versuche es erneut.'),
        ...(detail ? { detail } : {}),
      });
    } finally {
      setBusy(false);
    }
  };

  if (state.kind === 'failed-start') {
    return (
      <div className="alt-login">
        <LoginError message={state.message} detail={state.detail} />
        <button type="button" className="btn btn--secondary btn--sm" onClick={() => setAttempt((n) => n + 1)}>
          <RefreshCw size={16} aria-hidden="true" />
          Erneut versuchen
        </button>
      </div>
    );
  }

  return (
    <form className="alt-login" onSubmit={submit} noValidate>
      <p className="alt-login__intro">{CODEX_TEXTS.browserIntro}</p>
      <ol className="device__steps device__steps--compact">
        <li>
          <span className="device__num" aria-hidden="true">
            1
          </span>
          <div className="device__step-body">
            <p>Melde dich bei ChatGPT an:</p>
            <a
              className={`btn btn--secondary btn--sm${login ? '' : ' is-disabled'}`}
              href={login?.authorizeUrl ?? '#'}
              target="_blank"
              rel="noopener noreferrer"
              aria-disabled={!login}
              onClick={(event) => {
                if (!login) event.preventDefault();
                // Back in this tab, the address can be pasted right away.
                else inputRef.current?.focus({ preventScroll: true });
              }}
            >
              {login ? <ExternalLink size={16} aria-hidden="true" /> : <LoaderCircle size={16} className="spin" aria-hidden="true" />}
              Anmeldeseite öffnen
            </a>
          </div>
        </li>
        <li>
          <span className="device__num" aria-hidden="true">
            2
          </span>
          <div className="device__step-body">
            <p>{CODEX_TEXTS.browserStep2}</p>
          </div>
        </li>
        <li>
          <span className="device__num" aria-hidden="true">
            3
          </span>
          <div className="device__step-body device__step-body--wide">
            <label htmlFor={fieldId}>Adresse hier einfügen:</label>
            <div className="alt-login__row">
              <input
                id={fieldId}
                className="field__input field__input--mono"
                type="url"
                inputMode="url"
                autoComplete="off"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                placeholder="http://127.0.0.1:1455/auth/callback?code=…"
                value={url}
                onChange={(event) => setUrl(event.target.value)}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? errorId : undefined}
                ref={inputRef}
              />
              <button type="submit" className="btn btn--primary" disabled={busy || !login || !url.trim()}>
                {busy && <LoaderCircle size={18} className="spin" aria-hidden="true" />}
                {busy ? 'Prüfe …' : 'Verbinden'}
              </button>
            </div>
          </div>
        </li>
      </ol>
      {error && (
        <LoginError id={errorId} message={error.message} detail={error.detail}>
          {error.restart && (
            <button
              type="button"
              className="link-btn inline-error__action"
              onClick={() => {
                setUrl('');
                setAttempt((n) => n + 1);
              }}
            >
              <RefreshCw size={15} aria-hidden="true" />
              Anmeldung neu starten
            </button>
          )}
        </LoginError>
      )}
    </form>
  );
}

// ---------------------------------------------------------------------------

function AuthJsonLogin({ onConnected }: { onConnected: (result: CodexConnectedResult) => void }) {
  const [text, setText] = useState('');
  const [error, setError] = useState<{ message: string; detail?: string | undefined } | null>(null);
  const [busy, setBusy] = useState(false);
  const fieldId = useId();
  const errorId = useId();

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    let parsed: unknown;
    try {
      parsed = JSON.parse(text.trim());
    } catch {
      setError({ message: 'Das ist kein gültiges JSON. Kopiere bitte den vollständigen Inhalt der Datei.' });
      return;
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      setError({ message: 'Die Datei sollte ein JSON-Objekt enthalten (beginnt mit „{“).' });
      return;
    }
    setBusy(true);
    try {
      const result: CodexAuthResponse = await saveCodexAuth(parsed as CodexAuthBlob);
      onConnected({ account: result.account, ...(result.auth ? { auth: result.auth } : {}) });
    } catch (err) {
      const code = err instanceof ApiRequestError ? err.code : 'internal';
      setError({
        message:
          code === 'codex_auth_invalid' || code === 'bad_request'
            ? 'Diese Anmeldung ist ungültig oder abgelaufen. Melde dich in der Codex CLI neu an („codex login“) und versuche es erneut.'
            : requestFailureText(err, 'Die Anmeldung konnte nicht geprüft werden. Bitte versuche es erneut.'),
        detail: technicalDetail(err),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="alt-login" onSubmit={submit} noValidate>
      <p className="alt-login__note">
        <CircleAlert size={16} aria-hidden="true" />
        <span>{CODEX_TEXTS.authJsonRotation}</span>
      </p>
      <label htmlFor={fieldId} className="field__label">
        Inhalt von <code>~/.codex/auth.json</code>
      </label>
      <textarea
        id={fieldId}
        className="field__input field__input--mono"
        rows={5}
        value={text}
        onChange={(event) => setText(event.target.value)}
        placeholder={'{\n  "tokens": { … }\n}'}
        spellCheck={false}
        autoCapitalize="none"
        autoCorrect="off"
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
      />
      {error && <LoginError id={errorId} message={error.message} detail={error.detail} />}
      <div className="alt-login__footer">
        <p className="alt-login__subtle">{CODEX_TEXTS.authJsonWarning}</p>
        <button type="submit" className="btn btn--primary" disabled={busy || !text.trim()}>
          {busy && <LoaderCircle size={18} className="spin" aria-hidden="true" />}
          {busy ? 'Wird geprüft …' : 'Verbinden'}
        </button>
      </div>
    </form>
  );
}
